import pb from '../lib/pocketbase';
import type { Notification, NotificationType, NotificationPriority, NotificationScope } from '../types';

/**
 * Notification Service
 * 
 * Handles all notification-related operations including:
 * - Creating notifications
 * - Fetching notifications
 * - Marking as read/unread
 * - Deleting notifications
 * - Real-time subscription to new notifications
 */

export interface CreateNotificationParams {
  user_id: string;
  type: NotificationType;
  title: string;
  message: string;
  link?: string;
  metadata?: Record<string, any>;
  priority?: NotificationPriority;
  notification_scope?: NotificationScope;
  partner_id?: string;
}

class NotificationService {
  private collection = 'notifications';

  /**
   * Create a new notification
   */
  async createNotification(params: CreateNotificationParams): Promise<{ data: Notification | null; error: string | null }> {
    try {
      const notificationData = {
        user_id: params.user_id,
        type: params.type,
        title: params.title,
        message: params.message,
        read: false,
        link: params.link || '',
        metadata: params.metadata ? JSON.stringify(params.metadata) : '',
        priority: params.priority || 'normal',
        notification_scope: params.notification_scope || 'user',
        partner_id: params.partner_id || ''
      };

      console.log('Creating notification:', notificationData);

      const record = await pb.collection(this.collection).create(notificationData);

      const notification: Notification = {
        id: record.id,
        user_id: record.user_id,
        type: record.type,
        title: record.title,
        message: record.message,
        read: record.read,
        link: record.link || undefined,
        metadata: record.metadata ? (typeof record.metadata === 'string' ? JSON.parse(record.metadata) : record.metadata) : undefined,
        priority: record.priority,
        notification_scope: record.notification_scope || 'user',
        partner_id: record.partner_id || undefined,
        created: record.created,
        updated: record.updated
      };

      return { data: notification, error: null };
    } catch (error: any) {
      console.error('Failed to create notification:', error);
      return { data: null, error: error.message || 'Failed to create notification' };
    }
  }

  /**
   * Get all notifications for a user
   */
  async getUserNotifications(userId: string, unreadOnly: boolean = false): Promise<{ data: Notification[]; error: string | null }> {
    try {
      // Do not filter by user_id on the client side.
      // Rely on PocketBase RLS rules to return only notifications visible to the current user.
      let filter = '';
      if (unreadOnly) {
        filter = 'read = false';
      }

      const records = await pb.collection(this.collection).getFullList({
        filter: filter || undefined,
        sort: '-created',
        $autoCancel: false
      });

      const notifications: Notification[] = records.map(record => ({
        id: record.id,
        user_id: record.user_id,
        type: record.type,
        title: record.title,
        message: record.message,
        read: record.read,
        link: record.link || undefined,
        metadata: record.metadata ? (typeof record.metadata === 'string' ? JSON.parse(record.metadata) : record.metadata) : undefined,
        priority: record.priority,
        notification_scope: record.notification_scope || 'user',
        partner_id: record.partner_id || undefined,
        created: record.created,
        updated: record.updated
      }));

      return { data: notifications, error: null };
    } catch (error: any) {
      console.error('Failed to fetch notifications:', error);
      return { data: [], error: error.message || 'Failed to fetch notifications' };
    }
  }

  /**
   * Get unread notification count
   */
  async getUnreadCount(userId: string): Promise<{ count: number; error: string | null }> {
    try {
      // Do not filter by user_id on the client, rely on RLS.
      const records = await pb.collection(this.collection).getList(1, 1, {
        filter: 'read = false',
        $autoCancel: false
      });

      return { count: records.totalItems, error: null };
    } catch (error: any) {
      console.error('Failed to get unread count:', error);
      return { count: 0, error: error.message || 'Failed to get unread count' };
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string): Promise<{ success: boolean; error: string | null }> {
    try {
      await pb.collection(this.collection).update(notificationId, {
        read: true
      });

      return { success: true, error: null };
    } catch (error: any) {
      console.error('Failed to mark notification as read:', error);
      return { success: false, error: error.message || 'Failed to mark as read' };
    }
  }

  /**
   * Mark all notifications as read for a user
   */
  async markAllAsRead(userId: string): Promise<{ success: boolean; error: string | null }> {
    try {
      // Get all unread notifications visible to this user (RLS enforced on the server)
      const { data: notifications } = await this.getUserNotifications(userId, true);

      // Mark each as read
      const promises = notifications.map(notification =>
        pb.collection(this.collection).update(notification.id, { read: true })
      );

      await Promise.all(promises);

      return { success: true, error: null };
    } catch (error: any) {
      console.error('Failed to mark all as read:', error);
      return { success: false, error: error.message || 'Failed to mark all as read' };
    }
  }

  /**
   * Delete a notification
   */
  async deleteNotification(notificationId: string): Promise<{ success: boolean; error: string | null }> {
    try {
      await pb.collection(this.collection).delete(notificationId);
      return { success: true, error: null };
    } catch (error: any) {
      console.error('Failed to delete notification:', error);
      return { success: false, error: error.message || 'Failed to delete notification' };
    }
  }

  /**
   * Delete all read notifications for a user
   */
  async deleteAllRead(userId: string): Promise<{ success: boolean; error: string | null }> {
    try {
      // Fetch all read notifications visible to this user
      const { data: notifications } = await this.getUserNotifications(userId, false);
      const readNotifications = notifications.filter(n => n.read);

      const promises = readNotifications.map(notification =>
        pb.collection(this.collection).delete(notification.id)
      );

      await Promise.all(promises);

      return { success: true, error: null };
    } catch (error: any) {
      console.error('Failed to delete read notifications:', error);
      return { success: false, error: error.message || 'Failed to delete read notifications' };
    }
  }

  /**
   * Subscribe to real-time notifications for a user
   */
  async subscribeToNotifications(
    userId: string,
    callback: (notification: Notification) => void
  ): Promise<() => void> {
    try {
      const unsubscribe = await pb.collection(this.collection).subscribe('*', (e) => {
        // PocketBase will only send events for records visible to the current auth user
        if (e.action === 'create') {
          const notification: Notification = {
            id: e.record.id,
            user_id: e.record.user_id,
            type: e.record.type,
            title: e.record.title,
            message: e.record.message,
            read: e.record.read,
            link: e.record.link || undefined,
            metadata: e.record.metadata ? (typeof e.record.metadata === 'string' ? JSON.parse(e.record.metadata) : e.record.metadata) : undefined,
            priority: e.record.priority,
            notification_scope: e.record.notification_scope || 'user',
            partner_id: e.record.partner_id || undefined,
            created: e.record.created,
            updated: e.record.updated
          };

          callback(notification);
        }
      });

      return unsubscribe;
    } catch (error) {
      console.error('Failed to subscribe to notifications:', error);
      return () => {}; // Return empty unsubscribe function
    }
  }

  /**
   * Helper: Create advertisement approved notification (tenant-wide)
   */
  async notifyAdApproved(userId: string, adName: string, adId: string, partnerId: string): Promise<void> {
    await this.createNotification({
      user_id: userId,
      type: 'ad_approved',
      title: 'Advertisement Approved ✅',
      message: `Your advertisement "${adName}" has been approved and is now live in campaigns.`,
      link: `/advertisements`,
      metadata: { ad_id: adId, ad_name: adName },
      priority: 'high',
      notification_scope: 'partner_tenant',
      partner_id: partnerId
    });
  }

  /**
   * Helper: Create advertisement rejected notification (tenant-wide)
   */
  async notifyAdRejected(userId: string, adName: string, adId: string, reason: string, partnerId: string): Promise<void> {
    await this.createNotification({
      user_id: userId,
      type: 'ad_rejected',
      title: 'Advertisement Rejected ❌',
      message: `Your advertisement "${adName}" was rejected. Reason: ${reason}`,
      link: `/advertisements`,
      metadata: { ad_id: adId, ad_name: adName, rejection_reason: reason },
      priority: 'high',
      notification_scope: 'partner_tenant',
      partner_id: partnerId
    });
  }

  /**
   * Helper: Create ad pending review notification (for global admin)
   */
  async notifyAdPendingReview(adminUserId: string, adName: string, adId: string, partnerName: string): Promise<void> {
    await this.createNotification({
      user_id: adminUserId,
      type: 'ad_pending_review',
      title: 'New Advertisement for Review 📋',
      message: `${partnerName} submitted "${adName}" for approval.`,
      link: `/approvals`,
      metadata: { ad_id: adId, ad_name: adName, partner_name: partnerName },
      priority: 'normal'
    });
  }

  /**
   * Helper: Create ad resubmitted notification (for global admin)
   */
  async notifyAdResubmitted(adminUserId: string, adName: string, adId: string, partnerName: string): Promise<void> {
    await this.createNotification({
      user_id: adminUserId,
      type: 'ad_resubmitted',
      title: 'Advertisement Re-submitted 🔄',
      message: `${partnerName} updated and re-submitted "${adName}" for review.`,
      link: `/approvals`,
      metadata: { ad_id: adId, ad_name: adName, partner_name: partnerName },
      priority: 'normal'
    });
  }

  /**
   * Helper: Create ad expiring soon notification (tenant-wide)
   */
  async notifyAdExpiringSoon(userId: string, adName: string, adId: string, daysRemaining: number, partnerId: string): Promise<void> {
    await this.createNotification({
      user_id: userId,
      type: 'ad_expiring_soon',
      title: 'Advertisement Expiring Soon ⏰',
      message: `Your advertisement "${adName}" will expire in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}.`,
      link: `/advertisements`,
      metadata: { ad_id: adId, ad_name: adName, days_remaining: daysRemaining },
      priority: 'normal',
      notification_scope: 'partner_tenant',
      partner_id: partnerId
    });
  }

  /**
   * Helper: Create ad expired notification (tenant-wide)
   */
  async notifyAdExpired(userId: string, adName: string, adId: string, partnerId: string): Promise<void> {
    await this.createNotification({
      user_id: userId,
      type: 'ad_expired',
      title: 'Advertisement Expired 🕐',
      message: `Your advertisement "${adName}" has expired and is no longer active.`,
      link: `/advertisements`,
      metadata: { ad_id: adId, ad_name: adName },
      priority: 'low',
      notification_scope: 'partner_tenant',
      partner_id: partnerId
    });
  }

  /**
   * Helper: Create quota warning notification (tenant-wide)
   */
  async notifyQuotaWarning(userId: string, priority: string, remaining: number, total: number, partnerId: string): Promise<void> {
    const usagePercent = ((total - remaining) / total) * 100;
    
    await this.createNotification({
      user_id: userId,
      type: 'quota_warning',
      title: 'Quota Warning ⚠️',
      message: `You've used ${usagePercent.toFixed(0)}% of your ${priority} priority quota. Only ${remaining} of ${total} slots remaining.`,
      link: `/dashboard`,
      metadata: { priority, remaining, total, usage_percent: usagePercent },
      priority: 'normal',
      notification_scope: 'partner_tenant',
      partner_id: partnerId
    });
  }

  /**
   * Helper: Create campaign published notification (tenant-wide)
   */
  async notifyCampaignPublished(userId: string, campaignName: string, campaignId: string, partnerId: string): Promise<void> {
    await this.createNotification({
      user_id: userId,
      type: 'campaign_published',
      title: 'Campaign Published 🚀',
      message: `Your campaign "${campaignName}" is now live and visible to customers.`,
      link: `/campaigns`,
      metadata: { campaign_id: campaignId, campaign_name: campaignName },
      priority: 'normal',
      notification_scope: 'partner_tenant',
      partner_id: partnerId
    });
  }
}

export const notificationService = new NotificationService();
